export type DependencyGraph = ReadonlyMap<string, ReadonlySet<string>>;

/**
 * Returns true if adding `fromRow → toRow` would create a cycle in the
 * dependency graph. Implemented as a DFS from `toRow` looking for a
 * path back to `fromRow`.
 *
 * Self-links (`fromRow === toRow`) always cycle.
 */
export function wouldCreateCycle(
  graph: DependencyGraph,
  fromRow: string,
  toRow: string
): boolean {
  if (fromRow === toRow) return true;

  const visited = new Set<string>();
  const stack: string[] = [toRow];

  while (stack.length > 0) {
    const current = stack.pop() as string;
    if (current === fromRow) return true;
    if (visited.has(current)) continue;
    visited.add(current);
    const outgoing = graph.get(current);
    if (!outgoing) continue;
    for (const next of outgoing) {
      stack.push(next);
    }
  }

  return false;
}

/**
 * Full cycle scan. Returns the first cycle found (as an ordered list of
 * row ids where the last entry closes the loop), or `null` if the graph
 * is acyclic. Used by diagnostics / import flows, not by the drag
 * validator (which uses {@link wouldCreateCycle}).
 */
export function findAnyCycle(graph: DependencyGraph): string[] | null {
  const WHITE = 0;
  const GRAY = 1;
  const BLACK = 2;
  const color = new Map<string, number>();
  const parent = new Map<string, string | null>();

  const nodes = [...graph.keys()];

  for (const start of nodes) {
    if (color.get(start) === BLACK) continue;

    const stack: Array<{ node: string; iter: Iterator<string> }> = [];
    color.set(start, GRAY);
    parent.set(start, null);
    stack.push({
      node: start,
      iter: (graph.get(start) ?? new Set<string>())[Symbol.iterator](),
    });

    while (stack.length > 0) {
      const top = stack[stack.length - 1];
      if (!top) break;
      const next = top.iter.next();
      if (next.done) {
        color.set(top.node, BLACK);
        stack.pop();
        continue;
      }
      const neighbor: string = next.value;
      const neighborColor = color.get(neighbor) ?? WHITE;
      if (neighborColor === GRAY) {
        const cycle = [neighbor];
        let cur: string | null = top.node;
        while (cur && cur !== neighbor) {
          cycle.push(cur);
          cur = parent.get(cur) ?? null;
        }
        cycle.push(neighbor);
        cycle.reverse();
        return cycle;
      }
      if (neighborColor === WHITE) {
        color.set(neighbor, GRAY);
        parent.set(neighbor, top.node);
        stack.push({
          node: neighbor,
          iter: (graph.get(neighbor) ?? new Set<string>())[Symbol.iterator](),
        });
      }
    }
  }

  return null;
}
